/**
 * Super Admin Layout Component
 *
 * Dedicated layout for super admin users with admin-specific navigation.
 * Shows platform-level controls and quick access to administrative functions.
 *
 * Standards: .equilateral-standards/frontend-development/frontend_standards.md
 */

import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Badge } from 'flowbite-react';
import {
  HiChartBar,
  HiUsers,
  HiOfficeBuilding,
  HiShieldCheck,
  HiCog,
  HiLogout,
  HiViewGrid
} from 'react-icons/hi';
import { useStatus } from '../contexts/StatusProvider';

interface SuperAdminLayoutProps {
  children: React.ReactNode;
}

const SuperAdminLayout: React.FC<SuperAdminLayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { Email_Address, logout, Has_Client_Context } = useStatus();

  const isActive = (path: string) => location.pathname === path;

  const navigation = [
    { name: 'Dashboard', path: '/super/dashboard', icon: HiChartBar },
    { name: 'Clients', path: '/super/clients', icon: HiOfficeBuilding },
    { name: 'Users', path: '/super/users', icon: HiUsers },
    { name: 'Organizations', path: '/super/organizations', icon: HiViewGrid },
  ];

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      navigate('/login');
    }
  };

  const handleSwitchToClientView = () => {
    if (Has_Client_Context) {
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Super Admin Header */}
      <nav className="bg-gradient-to-r from-purple-700 to-indigo-700 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              <div className="flex-shrink-0 flex items-center">
                <HiShieldCheck className="w-8 h-8 text-white mr-2" />
                <h1 className="text-xl font-bold text-white">Super Admin Portal</h1>
              </div>
              <div className="hidden sm:ml-8 sm:flex sm:space-x-4">
                {navigation.map((item) => {
                  const Icon = item.icon;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`${
                        isActive(item.path)
                          ? 'bg-purple-800 text-white'
                          : 'text-purple-100 hover:bg-purple-600 hover:text-white'
                      } inline-flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors`}
                    >
                      <Icon className="w-5 h-5 mr-2" />
                      {item.name}
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* User Menu */}
            <div className="flex items-center space-x-4">
              <Badge color="purple" size="lg" className="bg-purple-900">
                SUPER ADMIN
              </Badge>

              {Has_Client_Context && (
                <Button
                  size="sm"
                  color="light"
                  onClick={handleSwitchToClientView}
                >
                  Switch to Client View
                </Button>
              )}

              <div className="text-white text-sm">
                {Email_Address}
              </div>

              <Button
                size="sm"
                color="light"
                onClick={handleLogout}
              >
                <HiLogout className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Info Banner for Client Context */}
      {Has_Client_Context && (
        <div className="bg-blue-50 border-b border-blue-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <HiOfficeBuilding className="w-5 h-5 text-blue-600 mr-2" />
                <span className="text-sm text-blue-800">
                  <strong>Client Context Active:</strong> You can switch between super admin and client views
                </span>
              </div>
              <Button
                size="xs"
                color="light"
                onClick={() => {
                  // Note: For super admins, Has_Client_Context is always false
                  // so this button will never be shown
                  navigate('/super/dashboard');
                }}
              >
                Clear Client Context
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main role="main">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="text-center text-sm text-gray-500">
            <p>Equilateral AI Super Admin Portal</p>
            <p className="mt-1">Platform Management & Oversight</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default SuperAdminLayout;
